import { useState } from 'react';
import { Alert, App, Button, Card, Skeleton, Space, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@/lib/http/apiClient';
import { tariffApi } from '../api/tariffApi';
import { TariffForm } from '../components/TariffForm';
import { useTariff } from '../hooks/useTariff';
import { tariffToFormValues, toUpdateTariffBody, type TariffFormValues } from '../utils/tariffForm';

/**
 * Página de edición de Tarifa (`/tarifas/:tariffId/editar`, solo `SUPER_ADMIN`).
 * Carga el detalle con `useTariff` y lo mapea al formulario. El contrato no permite
 * cambiar `type`/`currency` en el PATCH, así que el formulario los muestra fijos.
 */
export function EditTariffPage() {
  const { tariffId } = useParams<{ tariffId: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { tariff, loading, error, notFound, refresh } = useTariff(tariffId);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const goToList = () => navigate('/tarifas');
  const goToDetail = () =>
    tariffId ? navigate(`/tarifas/${encodeURIComponent(tariffId)}`) : goToList();

  const handleSubmit = async (values: TariffFormValues) => {
    if (!tariffId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await tariffApi.update(tariffId, toUpdateTariffBody(values));
      message.success('Tarifa actualizada.');
      goToDetail();
    } catch (caught) {
      setSubmitError(caught instanceof ApiError ? caught.message : 'No se pudo actualizar la tarifa.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tarifas-page">
      <div className="tarifas-page__header">
        <Space align="center">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label="Volver al detalle de la tarifa"
            onClick={goToDetail}
          />
          <Typography.Title level={3} style={{ margin: 0 }}>
            Editar tarifa
          </Typography.Title>
        </Space>
      </div>

      {loading ? (
        <Card>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      ) : notFound ? (
        <Alert
          type="warning"
          showIcon
          message="No encontramos la tarifa"
          description="Puede que se haya eliminado o que el enlace esté incompleto."
          action={
            <Button size="small" onClick={goToList}>
              Volver al listado
            </Button>
          }
        />
      ) : error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar la tarifa"
          description={error}
          action={
            <Button size="small" onClick={refresh}>
              Reintentar
            </Button>
          }
        />
      ) : tariff ? (
        <>
          {submitError && (
            <Alert
              type="error"
              showIcon
              role="alert"
              message="No se pudo actualizar la tarifa"
              description={submitError}
            />
          )}
          <Card>
            <TariffForm
              mode="edit"
              initialValues={tariffToFormValues(tariff)}
              submitting={submitting}
              onSubmit={handleSubmit}
              onCancel={goToDetail}
            />
          </Card>
        </>
      ) : null}
    </div>
  );
}
